import { useCallback, useEffect, useRef, useState } from "react";
import {
  API_BASE_URL,
  ApiError,
  fetchDecisions,
  fetchErrors,
  fetchLogs,
  fetchOverview,
  fetchPnlHistory,
  fetchPositions,
  fetchSignals,
  fetchLivePrices,
} from "@/api/client";
import { carryLiveFields } from "@/lib/livePositions";
import type {
  AiDecisionFeedItem,
  DailyPnlDto,
  OverviewDto,
  PositionsDto,
  SignalsDto,
  SymbolSignal,
  TechnicalErrorDto,
} from "@/api/types";
const POLL_INTERVAL_MS = 10000;
const LIVE_PRICES_INTERVAL_MS = 5000;
const SSE_RECONNECT_DELAY_MS = 3000;
const MAX_ACCUMULATED_LOG_LINES = 2000;
interface DashboardStreamSnapshot {
  overview: OverviewDto;
  signals: SignalsDto;
  positions: PositionsDto;
  decisions: {
    decisions: AiDecisionFeedItem[];
  };
  pnlHistory: {
    history: DailyPnlDto[];
  };
  errors: {
    errors: TechnicalErrorDto[];
  };
  logs: {
    logs: TechnicalErrorDto[];
  };
  lastUpdatedAt: number;
}
export interface DashboardData {
  overview: OverviewDto | null;
  signals: SignalsDto | null;
  positions: PositionsDto | null;
  decisions: AiDecisionFeedItem[];
  pnlHistory: DailyPnlDto[];
  errors: TechnicalErrorDto[];
  logs: TechnicalErrorDto[];
  loading: boolean;
  lastUpdatedAt: number | null;
  refresh: () => Promise<void>;
}
export function useDashboardData(onUnauthorized: () => void): DashboardData {
  const [overview, setOverview] = useState<OverviewDto | null>(null);
  const [signals, setSignals] = useState<SignalsDto | null>(null);
  const [positions, setPositions] = useState<PositionsDto | null>(null);
  const [decisions, setDecisions] = useState<AiDecisionFeedItem[]>([]);
  const [pnlHistory, setPnlHistory] = useState<DailyPnlDto[]>([]);
  const [errors, setErrors] = useState<TechnicalErrorDto[]>([]);
  const [logs, setLogs] = useState<TechnicalErrorDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const streamRef = useRef<EventSource | null>(null);
  const livePricesRef = useRef<Map<string, number>>(new Map());
  const mergeLivePrices = useCallback((incoming: SignalsDto): SignalsDto => {
    if (livePricesRef.current.size === 0) return incoming;
    return {
      ...incoming,
      symbolSignals: incoming.symbolSignals.map((s: SymbolSignal) => ({
        ...s,
        price: livePricesRef.current.get(s.symbol) ?? s.price,
      })),
    };
  }, []);
  const appendLogs = useCallback((incoming: TechnicalErrorDto[]) => {
    if (incoming.length === 0) return;
    setLogs((prev) => {
      const seen = new Set(prev.map((l) => l.id));
      const merged = [...prev, ...incoming.filter((l) => !seen.has(l.id))];
      return merged.slice(-MAX_ACCUMULATED_LOG_LINES);
    });
  }, []);
  const refresh = useCallback(async () => {
    try {
      const [overviewRes, signalsRes, positionsRes, decisionsRes, pnlRes, errorsRes, logsRes] = await Promise.all([
        fetchOverview(),
        fetchSignals(),
        fetchPositions(),
        fetchDecisions(30),
        fetchPnlHistory(14),
        fetchErrors(),
        fetchLogs(200),
      ]);
      setOverview(overviewRes);
      setSignals(mergeLivePrices(signalsRes));
      setPositions((prev) => carryLiveFields(positionsRes, prev));
      setDecisions(decisionsRes.decisions);
      setPnlHistory(pnlRes.history);
      setErrors(errorsRes.errors);
      appendLogs(logsRes.logs);
      setLastUpdatedAt(Date.now());
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        onUnauthorized();
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [mergeLivePrices, onUnauthorized, appendLogs]);
  useEffect(() => {
    void refresh();
    let disposed = false;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    const connectStream = () => {
      if (disposed) return;
      const streamUrl = `${API_BASE_URL}/api/dashboard/stream`;
      const source = new EventSource(streamUrl, { withCredentials: true });
      source.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data) as DashboardStreamSnapshot;
          setOverview(payload.overview);
          setSignals(mergeLivePrices(payload.signals));
          setPositions((prev) => carryLiveFields(payload.positions, prev));
          setDecisions(payload.decisions.decisions);
          setPnlHistory(payload.pnlHistory.history);
          setErrors(payload.errors.errors);
          appendLogs(payload.logs?.logs ?? []);
          setLastUpdatedAt(payload.lastUpdatedAt);
        } catch {}
      };
      source.onerror = () => {
        source.close();
        if (!disposed) {
          reconnectTimeout = setTimeout(connectStream, SSE_RECONNECT_DELAY_MS);
        }
      };
      streamRef.current = source;
    };
    connectStream();
    const interval = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    const livePricesInterval = setInterval(async () => {
      try {
        const livePrices = await fetchLivePrices();
        (livePrices.futures ?? []).forEach((p) => livePricesRef.current.set(p.symbol, p.price));
        setSignals((prev) => (prev ? mergeLivePrices(prev) : prev));
      } catch {}
    }, LIVE_PRICES_INTERVAL_MS);
    return () => {
      disposed = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      clearInterval(interval);
      clearInterval(livePricesInterval);
      streamRef.current?.close();
    };
  }, [refresh, mergeLivePrices, appendLogs]);
  return { overview, signals, positions, decisions, pnlHistory, errors, logs, loading, lastUpdatedAt, refresh };
}
